import Icon from '@Components/Icons/Icon/Icon'
import unitTypeColors from '@Constants/UnitTypeColors'
import { Box, FlexLayout } from '@eduact/ed-system'
import { ContentConfig } from '@eduact/utils'
import { useGetLearningTimeQuery } from '@Features/Dashboard/Classrooms/Components/Tabs/SingleClassroomOverview/SingleClassroomSlice/SingleClassroomSlice'
import { ResponsivePie } from '@nivo/pie'
import formatDuration from '@Utilities/Duration/formatDuration'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BounceLoader } from 'react-spinners'
import styled from 'styled-components'
import LearningTimeImage from '@Features/Dashboard/Overview/Assets/Images/Learning_Time.png'

const UNIT_TYPES = ['webinar', 'test', 'document', 'video', 'webcontent', 'assignment'] as const

export type LearningTimeScope = { user_id?: number; classroom_id?: number }

type Props = {
	scope: LearningTimeScope
}

/**
 * Single learning-time breakdown donut serving both scopes (student
 * dashboard by user_id, classroom overview by classroom_id). Center shows
 * the formatted total; tooltips show type, formatted time and % of total.
 */
const LearningTimeDonut: React.FC<Props> = ({ scope }) => {
	const [t] = useTranslation('course')
	// Neither id resolved yet — the dashboard widget derives user_id from the
	// profile, so it passes Number(undefined) === NaN until that query lands.
	// The server would actually answer a null id fine (it takes identity from
	// the session and ignores the body's user_id), but letting the call go out
	// would mint a junk NaN-keyed cache entry plus a second request once the
	// real id arrives. Hold the query — and treat the held window as LOADING,
	// not empty: "still resolving" and "genuinely zero minutes" must not paint
	// the same pixels.
	const scopePending = !scope.user_id && !scope.classroom_id
	const { data: learningData, isLoading, isFetching } = useGetLearningTimeQuery(scope as any, {
		skip: scopePending,
	})
	const pending = scopePending || isLoading || (isFetching && !learningData)

	const pieChartData = useMemo(() => {
		if (!learningData) return undefined
		return UNIT_TYPES.map(type => ({
			id: type,
			label: type,
			value: (learningData.data as any)[type] ?? 0,
			color: unitTypeColors[type],
		}))
	}, [learningData])

	const total = useMemo(() => {
		return pieChartData?.reduce((sum, node) => sum + node.value, 0) ?? 0
	}, [pieChartData])

	const CenterTotal = ({ centerX, centerY }: any) => (
		<text x={centerX} y={centerY} textAnchor="middle" dominantBaseline="central" style={{ fontSize: '0.9rem', fontWeight: 700 }}>
			{formatDuration(total)}
		</text>
	)

	return (
		<Wrapper flexDirection="row" alignItems="center">
			{pending ? (
				<EmptyWrapper>
					<BounceLoader loading size={40} color="#6c63ff" />
				</EmptyWrapper>
			) : pieChartData && total > 0 ? (
				<Box height={'100%'} width="100%">
					<ResponsivePie
						data={pieChartData}
						innerRadius={0.7}
						padAngle={0.7}
						margin={{ left: 20, right: 20 }}
						activeOuterRadiusOffset={8}
						colors={{ datum: 'data.color' }}
						enableArcLinkLabels={false}
						isInteractive
						fit
						animate={false}
						motionConfig="slow"
						valueFormat={() => null as any}
						layers={['arcs', CenterTotal]}
						tooltip={({ datum }) => {
							const type = datum.data.label as string
							const percent = total > 0 ? Math.round((datum.data.value / total) * 100) : 0
							return (
								<SliceContainer>
									<SliceItemWrapper>
										<FlexLayout alignItems="center" gridGap="0.3rem">
											<Icon>{ContentConfig[type as UnitType]?.mdIcon}</Icon>
											<span>{type}</span>
										</FlexLayout>
									</SliceItemWrapper>
									<SliceItemWrapper>
										<span>
											{formatDuration(datum.data.value)} ({percent}%)
										</span>
									</SliceItemWrapper>
								</SliceContainer>
							)
						}}
					/>
				</Box>
			) : (
				<EmptyWrapper>
					<img alt="learning time" src={LearningTimeImage} />
					<EmptyText>{t('no_learning_time')}</EmptyText>
				</EmptyWrapper>
			)}
			<LegendWrapper>
				{pieChartData?.map(item => (
					<FlexLayout key={item.id} gridGap="0.5rem" alignItems="center" height="1rem">
						<Dot color={item.color}>•</Dot>
						<LegendText color={item.color}>{item.label}</LegendText>
					</FlexLayout>
				))}
			</LegendWrapper>
		</Wrapper>
	)
}

const Wrapper = styled(FlexLayout)`
	background-color: #fff;
	border-radius: 5px;
	width: 100%;
	height: 250px;
	padding: 0.513rem 0.825rem;
	${({ theme }) => `${theme.mediaQueries.large}{
		padding: 0.963rem 0.556rem;
    	border-radius: 15px;
    	min-height: 18.55rem;
		min-width: 18.938rem;
	}`}
`
const LegendWrapper = styled(FlexLayout)`
	flex-direction: column;
`
const Dot = styled.span<{ color: string | undefined }>`
	font-size: 2.5rem;
	color: ${({ color }) => color};
`
const LegendText = styled.span<{ color: string | undefined }>`
	color: ${({ color }) => color};
	font-size: 0.7rem;
	font-weight: 600;
	margin-top: 5px;
`
const SliceContainer = styled(FlexLayout)`
	border: 2px solid ${props => props.theme.colors.primary};
	background: ${props => props.theme.colors.light};
	padding: 8px;
	border-radius: 10px;
	display: grid;
	row-gap: 10px;
`
const SliceItemWrapper = styled(FlexLayout)`
	font-size: 0.9rem;
	font-weight: 600;
	gap: 0.2rem;
	color: ${props => props.theme.colors.spanishGray};
	text-transform: capitalize;
	display: flex;
	align-items: center;
	column-gap: 5px;
	min-width: 7rem;
`
const EmptyWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	img {
		max-width: 60%;
	}
`
const EmptyText = styled.p`
	font-size: 0.8rem;
	font-weight: 600;
	color: ${props => props.theme.colors.spanishGray};
`

export default LearningTimeDonut
